/**
 * Product Media Gallery Handler
 * Manages variant switching and image display for product media galleries
 */
(() => {
  // Configuration
  const CONFIG = {
    enablePreselection: window.section?.enable_preselection,
    selectors: {
      variantsParent: '.js-product-variants-parent',
      firstGridImage: '.product-media-gallery__grid-image1 img',
      productImagesJson: '#product-images-json',
      galleryContainer: "[class^='product-media-gallery__grid-layout']",
      galleryImages: '.product-media-gallery__grid-image img',
      galleryImageContainer: '.product-media-gallery__grid-image',
      swiperContainer: '.product-media-gallery--mobile .product-media-gallery__swiper-wrapper',
      swiperInstance: '.js-product-media-gallery.swiper',
      desktopGallery: ".product-media-gallery--desktop [class^='product-media-gallery__grid-layout']",
      checkedRadio: "input[type='radio']:checked"
    },
    classes: {
      gridLayout: 'product-media-gallery__grid-layout2',
      swiperSlide: 'swiper-slide product-media-gallery__swiper-slide js-product-media-gallery__swiper-slide'
    }
  };

  // DOM Elements
  const elements = {
    variantsParent: document.querySelector(CONFIG.selectors.variantsParent),
    firstGridImage: document.querySelector(CONFIG.selectors.firstGridImage),
    productImagesJson: document.getElementById('product-images-json')
  };

  // Image Management Functions
  /**
   * Swaps the main product image with the new image
   * @param {Object} newImage - Image object with src and srcset properties
   */
  const switchMainImage = (newImage) => {
    if (!elements.firstGridImage) {
      return;
    }
    
    // Find the gallery image element that matches the newImage src
    const galleryContainer = document.querySelector(CONFIG.selectors.galleryContainer);
    if (!galleryContainer) {
      return;
    }
    
    const galleryImages = galleryContainer.querySelectorAll(CONFIG.selectors.galleryImages);
    
    const targetGalleryImage = Array.from(galleryImages).find(img => {
      // Normalize both URLs to handle protocol differences (https:// vs //)
      const normalizeUrl = (url) => url.split('?')[0].replace(/^https?:/, '');
      const galleryUrl = normalizeUrl(img.src);
      const newImageUrl = normalizeUrl(newImage.src);
      
      return galleryUrl === newImageUrl;
    });
    
    if (!targetGalleryImage) {
      // Fallback: Just replace the main image without swapping
      elements.firstGridImage.src = newImage.src;
      elements.firstGridImage.srcset = newImage.srcset || '';
      return;
    }
    
    // Store the current main image data
    const currentMainSrc = elements.firstGridImage.src;
    const currentMainSrcset = elements.firstGridImage.srcset;
    
    // Swap: Set main image to new image
    elements.firstGridImage.src = newImage.src;
    elements.firstGridImage.srcset = newImage.srcset || '';
    
    // Swap: Set gallery image to old main image
    targetGalleryImage.src = currentMainSrc;
    targetGalleryImage.srcset = currentMainSrcset;
  };

  // Variant Handling Functions
  /**
   * Gets variant data from the associated script element
   * @param {string} variantId - ID of the variant input
   * @returns {Object|null} Parsed variant data or null
   */
  const getVariantData = (variantId) => {
    const scriptElement = document.querySelector(
      `script[type="application/json"][data-resource="${variantId}"]`
    );
    
    if (!scriptElement) return null;
    
    try {
      return JSON.parse(scriptElement.textContent);
    } catch (error) {
      console.error('Error parsing variant data:', error);
      return null;
    }
  };

  /**
   * Handles variant switch event
   * @param {Event} event - Change event from variant input
   */
  const handleVariantSwitch = (event) => {
    const checkedInput = event.target;
    
    if (!checkedInput.checked) return;

    const variantData = getVariantData(checkedInput.id);
    
    if (variantData?.featured_image) {
      switchMainImage(variantData.featured_image);
    }
  };

  /**
   * Initializes the gallery with the default variant or first image
   */
  const initializeDefaultVariant = () => {
    const checkedInput = elements.variantsParent?.querySelector(CONFIG.selectors.checkedRadio);

    if (checkedInput) {
      // Initialize with selected variant
      const variantData = getVariantData(checkedInput.id);
      
      if (variantData?.featured_image) {
        switchMainImage(variantData.featured_image);
        return;
      }
    }

    // Fallback to product images
    if (!elements.productImagesJson) return;

    try {
      const productImages = JSON.parse(elements.productImagesJson.textContent);
      
      if (productImages?.images?.length > 0) {
        const firstImage = productImages.images[0];
        switchMainImage(firstImage);
      }
    } catch (error) {
      console.error('Error parsing product images:', error);
    }
  };

  // Initialization
  /**
   * Sets up event listeners and initializes the gallery
   */
  const initialize = () => {
    if (!elements.variantsParent) return;

    // Add variant change listener
    elements.variantsParent.addEventListener('change', handleVariantSwitch);

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeDefaultVariant);
    } else {
      initializeDefaultVariant();
    }
  };

  // Start the application
  initialize();
})();
